/**
 * ORW Product Main -- Interactivity
 *
 * Works for every product page:
 * - Thumbnail image switching (crossfade)
 * - Color swatch variant selection (updates price, image, URL, availability)
 * - Quantity stepper (min 1, max 10)
 * - Add to Cart (AJAX with error handling)
 * - Buy Now (redirect to checkout)
 * - Accordion toggle
 * - Sold out state handling
 * - Product data caching (single fetch)
 */

class ORWProductMain {
  constructor(container) {
    this.container = container;
    this.quantity = 1;
    this.currentVariantId = null;
    this.productData = null;
    this.addCartLabel = '';
    this.buyNowLabel = '';

    this.mainImage = container.querySelector('[data-orw-main-image]');
    this.thumbs = container.querySelectorAll('[data-orw-thumb]');
    this.prevBtn = container.querySelector('[data-orw-nav-prev]');
    this.nextBtn = container.querySelector('[data-orw-nav-next]');
    this.swatches = container.querySelectorAll('[data-orw-swatch]');
    this.qtyValue = container.querySelector('[data-orw-qty-value]');
    this.qtyMinus = container.querySelector('[data-orw-qty-minus]');
    this.qtyPlus = container.querySelector('[data-orw-qty-plus]');
    this.addCartBtn = container.querySelector('[data-orw-add-cart]');
    this.buyNowBtn = container.querySelector('[data-orw-buy-now]');
    this.accordionToggles = container.querySelectorAll('[data-orw-accordion-toggle]');

    if (this.addCartBtn) {
      this.currentVariantId = this.addCartBtn.dataset.variantId;
      this.addCartLabel = this.addCartBtn.textContent.trim();
    }
    if (this.buyNowBtn) {
      this.buyNowLabel = this.buyNowBtn.textContent.trim();
    }

    // Cache product data on init
    this.loadProductData();
    this.bindEvents();
  }

  loadProductData() {
    var productUrl = this.container.dataset.productUrl;
    if (!productUrl) return;

    var self = this;
    fetch(productUrl)
      .then(function(res) { return res.json(); })
      .then(function(product) {
        self.productData = product;
      })
      .catch(function(err) {
        console.error('ORW Product: failed to load product data', err);
      });
  }

  bindEvents() {
    var self = this;

    this.thumbs.forEach(function(thumb) {
      thumb.addEventListener('click', self.onThumbClick.bind(self));
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', this.onNavPrev.bind(this));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', this.onNavNext.bind(this));
    }
    // Set initial disabled state for nav arrows
    this.updateNavState();

    /* On load, the first image is active — mark image-main accordingly so the
       gradient backdrop shadow is visible. JS toggles this on thumb clicks. */
    var imageMainInit = this.container.querySelector('.orw-product-main__image-main');
    if (imageMainInit) imageMainInit.classList.add('is-first-image');

    this.swatches.forEach(function(swatch) {
      swatch.addEventListener('click', self.onSwatchClick.bind(self));
    });

    /* Swatch rail prev/next buttons. Visibility:
       - prev: hidden at start (scrollLeft === 0) or no overflow
       - next: hidden at end or no overflow
       Click scrolls by ~5 swatches in the chosen direction. */
    var swatchRail = this.container.querySelector('[data-orw-swatches]');
    var swatchPrev = this.container.querySelector('[data-orw-swatch-prev]');
    var swatchNext = this.container.querySelector('[data-orw-swatch-next]');
    if (swatchRail && (swatchPrev || swatchNext)) {
      var SWATCH_STEP = 5 * (62 + 0.75 * 2 + 10); // 5 swatches incl. border + gap
      var updateArrows = function() {
        var hasOverflow = swatchRail.scrollWidth > swatchRail.clientWidth + 2;
        var atStart = swatchRail.scrollLeft <= 1;
        var atEnd = swatchRail.scrollLeft + swatchRail.clientWidth >= swatchRail.scrollWidth - 2;
        if (swatchPrev) swatchPrev.hidden = !hasOverflow || atStart;
        if (swatchNext) swatchNext.hidden = !hasOverflow || atEnd;
      };
      if (swatchPrev) {
        swatchPrev.addEventListener('click', function() {
          swatchRail.scrollBy({ left: -SWATCH_STEP, behavior: 'smooth' });
        });
      }
      if (swatchNext) {
        swatchNext.addEventListener('click', function() {
          swatchRail.scrollBy({ left: SWATCH_STEP, behavior: 'smooth' });
        });
      }
      swatchRail.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', updateArrows);
      /* initial check after layout settles (images may shift width) */
      requestAnimationFrame(updateArrows);
      setTimeout(updateArrows, 300);
    }

    if (this.qtyMinus) {
      this.qtyMinus.addEventListener('click', this.decreaseQty.bind(this));
    }
    if (this.qtyPlus) {
      this.qtyPlus.addEventListener('click', this.increaseQty.bind(this));
    }

    if (this.addCartBtn) {
      this.addCartBtn.addEventListener('click', this.addToCart.bind(this));
    }

    if (this.buyNowBtn) {
      this.buyNowBtn.addEventListener('click', this.buyNow.bind(this));
    }

    this.accordionToggles.forEach(function(toggle) {
      toggle.addEventListener('click', self.onAccordionToggle.bind(self));
    });

    this.bindTouchSwipe();
    this.preloadSiblings();
  }

  /* ========== SIBLING PAGE PRELOAD ==========
     Fetch all sibling product pages in the background right after init,
     stash the HTML in an in-memory cache shared via window.__orwSiblingCache.
     On family-swatch click, swapToSiblingProduct() reads from the cache —
     no network roundtrip = ~instant swap. Loaded with priority "low" so
     the active page paints first. */
  preloadSiblings() {
    if (!window.__orwSiblingCache) window.__orwSiblingCache = new Map();
    var cache = window.__orwSiblingCache;
    var urls = [];
    this.swatches.forEach(function(s) {
      var u = s.dataset.orwSwatchUrl || (s.tagName === 'A' ? s.getAttribute('href') : null);
      if (u && !cache.has(u) && u !== window.location.pathname) urls.push(u);
    });
    if (!urls.length) return;
    /* Defer to idle so it doesn't compete with critical paint */
    var run = function() {
      urls.forEach(function(u) {
        if (cache.has(u)) return;
        cache.set(u, 'PENDING');
        fetch(u, { credentials: 'same-origin', priority: 'low' })
          .then(function(r) { return r.text(); })
          .then(function(html) { cache.set(u, html); })
          .catch(function() { cache.delete(u); });
      });
    };
    if (window.requestIdleCallback) requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 200);
  }

  /* ========== TOUCH SWIPE (mobile) ==========
     Horizontal swipe on the main image area = step prev/next. Only
     touch input — mouse/wheel left untouched (no page-scroll hijack). */
  bindTouchSwipe() {
    var imageMain = this.container.querySelector('.orw-product-main__image-main');
    if (!imageMain || this.thumbs.length < 2) return;

    var self = this;
    var startX = 0, startY = 0, tracking = false;
    var THRESHOLD = 40; // px

    imageMain.addEventListener('touchstart', function(e) {
      if (e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      tracking = true;
    }, { passive: true });

    imageMain.addEventListener('touchend', function(e) {
      if (!tracking) return;
      tracking = false;
      var t = e.changedTouches[0];
      if (!t) return;
      var dx = t.clientX - startX;
      var dy = t.clientY - startY;
      // Ignore mostly-vertical swipes (let the page scroll)
      if (Math.abs(dy) > Math.abs(dx)) return;
      if (Math.abs(dx) < THRESHOLD) return;
      var idx = self.findActiveThumbIndex();
      var len = self.thumbs.length;
      var nextIdx = dx < 0 ? (idx + 1) % len : (idx - 1 + len) % len;
      self.thumbs[nextIdx].click();
    }, { passive: true });
  }

  /* ========== THUMBNAILS ========== */

  onThumbClick(e) {
    var thumb = e.currentTarget;
    var imageUrl = thumb.dataset.imageUrl;
    var idx = Array.prototype.indexOf.call(this.thumbs, thumb);

    this.thumbs.forEach(function(t) {
      t.classList.remove('orw-product-main__thumb--active');
    });
    thumb.classList.add('orw-product-main__thumb--active');

    // Instant swap, no animation
    if (this.mainImage && imageUrl) {
      this.mainImage.src = imageUrl;
    }

    /* Toggle the first-image state: the gradient backdrop shadow is
       positioned for the front-facing watch. Side/back/profile shots
       look wrong with that shadow, so hide it whenever a non-first
       image is showing. */
    var imageMain = this.container.querySelector('.orw-product-main__image-main');
    if (imageMain) {
      if (idx === 0) imageMain.classList.add('is-first-image');
      else imageMain.classList.remove('is-first-image');
    }

    this.updateNavState();
  }

  /* ========== PREV / NEXT NAV ARROWS (mobile only) ========== */

  findActiveThumbIndex() {
    for (var i = 0; i < this.thumbs.length; i++) {
      if (this.thumbs[i].classList.contains('orw-product-main__thumb--active')) {
        return i;
      }
    }
    return 0;
  }

  onNavPrev(e) {
    e.preventDefault();
    var idx = this.findActiveThumbIndex();
    var len = this.thumbs.length;
    if (len < 2) return;
    var prev = (idx - 1 + len) % len;
    this.thumbs[prev].click();
  }

  onNavNext(e) {
    e.preventDefault();
    var idx = this.findActiveThumbIndex();
    var len = this.thumbs.length;
    if (len < 2) return;
    var next = (idx + 1) % len;
    this.thumbs[next].click();
  }

  updateNavState() {
    /* Prev/next loop infinitely — never disabled when there's >= 2 images. */
    if (!this.prevBtn && !this.nextBtn) return;
    var enable = this.thumbs.length >= 2;
    if (this.prevBtn) this.prevBtn.disabled = !enable;
    if (this.nextBtn) this.nextBtn.disabled = !enable;
  }

  /* ========== COLOR SWATCHES ========== */

  onSwatchClick(e) {
    var swatch = e.currentTarget;

    /* Family-swatch case: each variant is a SEPARATE product (not a Shopify
       variant), so the swatch is an <a href="{sibling-url}">. Default browser
       click would do a full-page navigation (slow). Intercept it and AJAX
       swap the .orw-product-main section in-place — feels instant because
       the sibling page was prefetched at render time. */
    var siblingUrl = swatch.dataset.orwSwatchUrl
      || (swatch.tagName === 'A' ? swatch.getAttribute('href') : null);

    if (siblingUrl && siblingUrl !== window.location.pathname && siblingUrl !== window.location.href) {
      e.preventDefault();
      this.swapToSiblingProduct(siblingUrl, swatch);
      return;
    }

    /* Same-product variant case (Shopify variants under one product):
       update variant ID + price + URL without navigation. */
    var variantId = swatch.dataset.variantId;
    this.swatches.forEach(function(s) {
      s.classList.remove('orw-product-main__swatch--active');
    });
    swatch.classList.add('orw-product-main__swatch--active');

    this.currentVariantId = variantId;
    if (this.addCartBtn) this.addCartBtn.dataset.variantId = variantId;
    if (this.buyNowBtn) this.buyNowBtn.dataset.variantId = variantId;

    var url = new URL(window.location.href);
    if (variantId) url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());

    // Price hints for the free gift (orw-free-gift.js): never leave the old
    // variant's price on the button
    [this.addCartBtn, this.buyNowBtn].forEach(function(btn) {
      if (btn) { delete btn.dataset.price; delete btn.dataset.compareAtPrice; }
    });

    if (this.productData) {
      var variant = this.findVariant(variantId);
      if (variant) {
        [this.addCartBtn, this.buyNowBtn].forEach(function(btn) {
          if (!btn) return;
          btn.dataset.price = variant.price;
          btn.dataset.compareAtPrice = variant.compare_at_price || 0;
        });
        this.updatePrice(variant);
        this.updateMainImage(variant);
        this.updateAvailability(variant);
      }
    }
  }

  /* ========== FAMILY-SWATCH AJAX SWAP ==========
     Fetch sibling product page, replace the entire .orw-product-main
     section with the sibling's, then re-init this controller on the new
     DOM. Browser cached HTML from <link rel="prefetch"> at render time
     so the fetch is near-instant. */
  swapToSiblingProduct(url, clickedSwatch) {
    /* Optimistic visual: mark the clicked swatch active before fetch */
    var clickedActive = clickedSwatch;
    this.swatches.forEach(function(s) {
      s.classList.remove('orw-product-main__swatch--active');
    });
    if (clickedActive) clickedActive.classList.add('orw-product-main__swatch--active');

    var self = this;

    /* Try in-memory cache first (populated by preloadSiblings) */
    var cache = window.__orwSiblingCache;
    var apply = function(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        /* Find the new product-main section in fetched doc */
        var oldSection = self.container.closest('section') || self.container;
        var newSection = doc.querySelector('section .orw-product-main')
          ? doc.querySelector('section .orw-product-main').closest('section')
          : doc.querySelector('.orw-product-main');
        if (!newSection || !oldSection) {
          /* Fallback to navigation if structure unexpected */
          window.location.href = url;
          return;
        }
        oldSection.replaceWith(newSection);
        /* Update browser title + URL without reload */
        var newTitleEl = doc.querySelector('title');
        if (newTitleEl) document.title = newTitleEl.textContent;
        history.pushState({ orwProductSwap: true }, '', url);
        /* Update breadcrumb */
        var newBreadcrumb = doc.querySelector('.orw-product-main__breadcrumb');
        var oldBreadcrumb = document.querySelector('.orw-product-main__breadcrumb');
        if (newBreadcrumb && oldBreadcrumb && newBreadcrumb !== oldBreadcrumb) {
          oldBreadcrumb.replaceWith(newBreadcrumb);
        }
        /* Re-init this controller on the new DOM */
        var freshContainer = document.querySelector('.orw-product-main');
        if (freshContainer && window.ORWProductMain) {
          new window.ORWProductMain(freshContainer);
        }
        /* Notify other scripts (wishlist hearts, etc.) to re-sync against
           the freshly injected DOM. */
        document.dispatchEvent(new CustomEvent('orw:product:swapped'));
        /* Restore scroll position so the page doesn't jump */
    };

    if (cache && cache.has(url) && cache.get(url) !== 'PENDING') {
      /* Instant from cache */
      try { apply(cache.get(url)); }
      catch (err) { window.location.href = url; }
      return;
    }

    /* Cache miss or still pending — fetch fresh */
    fetch(url, { credentials: 'same-origin' })
      .then(function(r) { return r.text(); })
      .then(function(html) {
        if (cache) cache.set(url, html);
        apply(html);
      })
      .catch(function() {
        window.location.href = url;
      });
  }

  findVariant(variantId) {
    if (!this.productData) return null;
    for (var i = 0; i < this.productData.variants.length; i++) {
      if (String(this.productData.variants[i].id) === String(variantId)) {
        return this.productData.variants[i];
      }
    }
    return null;
  }

  updatePrice(variant) {
    var priceEl = this.container.querySelector('[data-orw-price-value]');
    var compareEl = this.container.querySelector('[data-orw-compare-price]');

    if (priceEl) {
      priceEl.textContent = this.formatMoney(variant.price);
    }

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (compareEl) {
        compareEl.textContent = this.formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
      } else {
        var span = document.createElement('span');
        span.className = 'orw-product-main__compare-price';
        span.setAttribute('data-orw-compare-price', '');
        span.textContent = this.formatMoney(variant.compare_at_price);
        var priceWrap = this.container.querySelector('[data-orw-price]');
        if (priceWrap) priceWrap.appendChild(span);
      }
    } else if (compareEl) {
      compareEl.style.display = 'none';
    }
  }

  updateMainImage(variant) {
    if (!variant.featured_image || !this.mainImage) return;

    var newSrc = variant.featured_image.src.replace(/(\.[^.]+)$/, '_1200x$1');
    this.mainImage.style.opacity = '0';
    this.mainImage.style.transition = 'opacity 0.25s ease';
    var self = this;
    setTimeout(function() {
      self.mainImage.src = newSrc;
      self.mainImage.style.opacity = '1';
    }, 250);

    if (this.thumbs.length > 0) {
      var firstThumb = this.thumbs[0];
      firstThumb.dataset.imageUrl = newSrc;
      var thumbImg = firstThumb.querySelector('img');
      if (thumbImg) {
        thumbImg.src = variant.featured_image.src.replace(/(\.[^.]+)$/, '_240x$1');
      }
      this.thumbs.forEach(function(t) {
        t.classList.remove('orw-product-main__thumb--active');
      });
      firstThumb.classList.add('orw-product-main__thumb--active');
    }
  }

  updateAvailability(variant) {
    var available = variant.available;

    if (this.addCartBtn) {
      if (available) {
        this.addCartBtn.disabled = false;
        this.addCartBtn.textContent = this.addCartLabel || 'Add to cart';
        this.addCartBtn.classList.remove('orw-product-main__add-cart--disabled');
      } else {
        this.addCartBtn.disabled = true;
        this.addCartBtn.textContent = 'Sold Out';
        this.addCartBtn.classList.add('orw-product-main__add-cart--disabled');
      }
    }

    if (this.buyNowBtn) {
      if (available) {
        this.buyNowBtn.disabled = false;
        this.buyNowBtn.textContent = this.buyNowLabel || 'Buy Now';
        this.buyNowBtn.classList.remove('orw-product-main__buy-now--disabled');
      } else {
        this.buyNowBtn.disabled = true;
        this.buyNowBtn.textContent = 'Sold Out';
        this.buyNowBtn.classList.add('orw-product-main__buy-now--disabled');
      }
    }
  }

  /* ========== QUANTITY ========== */

  decreaseQty() {
    if (this.quantity > 1) {
      this.quantity--;
      this.updateQtyDisplay();
    }
  }

  increaseQty() {
    if (this.quantity < 10) {
      this.quantity++;
      this.updateQtyDisplay();
    }
  }

  updateQtyDisplay() {
    if (this.qtyValue) {
      this.qtyValue.textContent = this.quantity < 10 ? '0' + this.quantity : String(this.quantity);
    }
    if (this.qtyMinus) {
      this.qtyMinus.classList.toggle('orw-product-main__qty-btn--minus', this.quantity <= 1);
    }
  }

  /* ========== ADD TO CART ========== */

  addToCart() {
    if (!this.currentVariantId || (this.addCartBtn && this.addCartBtn.disabled)) return;

    var btn = this.addCartBtn;
    var originalText = btn.textContent;
    btn.textContent = 'Adding...';
    btn.disabled = true;

    this.addLine().then(function() {
      btn.textContent = 'Added!';
      setTimeout(function() {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1200);
    }, function(err) {
      btn.textContent = (err && err.description) ? err.description : 'Could not add to cart';
      setTimeout(function() {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 2000);
    });
  }

  /* One Ajax add with the chosen quantity. window.ORWCart (orw-cart-drawer.js,
     loaded on every page) opens the drawer and renders the new line from the
     /cart/add.js response. Fallback: Shopify's plain /cart/add URL. */
  addLine() {
    var line = { id: parseInt(this.currentVariantId, 10), quantity: this.quantity };
    if (window.ORWCart) return window.ORWCart.add([line]);
    window.location.href = '/cart/add?id=' + line.id + '&quantity=' + line.quantity;
    return new Promise(function() {});
  }

  /* ========== BUY NOW ========== */

  /* Buy Now flow per user spec:
     1. Click Buy Now -> add variant to cart via AJAX
     2. Open the Quick Cart drawer with the new item visible
     3. User clicks "Checkout" in the drawer (or "View Cart" -> /cart)
     So Buy Now behaves the same as the card + Add to Cart button. */
  buyNow() {
    if (!this.currentVariantId || (this.buyNowBtn && this.buyNowBtn.disabled)) return;
    var btn = this.buyNowBtn;
    if (btn) btn.disabled = true;

    var done = function() { if (btn) btn.disabled = false; };
    this.addLine().then(done, done);
  }

  /* ========== ACCORDIONS ========== */

  onAccordionToggle(e) {
    var header = e.currentTarget;
    var accordion = header.closest('[data-orw-accordion]');
    if (!accordion) return;

    accordion.classList.toggle('orw-product-main__accordion--open');
  }

  /* ========== HELPERS ========== */

  formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    var rupees = Math.floor(cents / 100);
    return '\u20B9 ' + rupees.toLocaleString('en-IN');
  }
}

/**
 * REMOVED 2026-05-14 per client direction: previously this function
 * applied a transform: scale() on .orw-product-main to fit shorter
 * viewports between 990 and 1728. Now the body-zoom JS in
 * theme.liquid handles ALL desktop scaling proportionally, so a
 * second scale transform here was double-scaling and producing the
 * empty side gutters seen in the client screenshot at 1440 wide.
 *
 * Kept as a no-op so the call sites below stay valid; if any
 * inline style was applied by an earlier version of this script,
 * clear it on init so the page recovers cleanly.
 */
function setupORWProductMainViewportFit() {
  var section = document.querySelector('.orw-product-main');
  if (!section) return;
  section.style.transform = '';
  section.style.transformOrigin = '';
  section.style.marginBottom = '';
}

/* Back-in-stock notify: customer_posted=true is shared by every customer
   form on the page (footer newsletter included). A sessionStorage flag set
   on submit is the only proof THIS form posted, so the success message is
   revealed only when the flag matches the product handle. */
function setupORWNotifyForm() {
  var form = document.getElementById('orw-notify-form');
  if (!form) return;
  var KEY = 'orw-notify-pending';
  var handle = form.getAttribute('data-product-handle') || '1';
  form.addEventListener('submit', function() {
    try { sessionStorage.setItem(KEY, handle); } catch (e) {}
  });
  var success = form.querySelector('[data-orw-notify-success]');
  if (!success) return;
  var pending = null;
  try { pending = sessionStorage.getItem(KEY); } catch (e) {}
  if (pending === handle) {
    success.hidden = false;
    var fields = form.querySelector('[data-orw-notify-fields]');
    if (fields) fields.hidden = true;
    try { sessionStorage.removeItem(KEY); } catch (e) {}
  }
}

/* Expose for AJAX swap re-init after family-swatch click. */
window.ORWProductMain = ORWProductMain;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    var el = document.querySelector('[data-orw-product]');
    if (el) new ORWProductMain(el);
    setupORWProductMainViewportFit();
    setupORWNotifyForm();
  });
} else {
  var el = document.querySelector('[data-orw-product]');
  if (el) new ORWProductMain(el);
  setupORWProductMainViewportFit();
  setupORWNotifyForm();
}

/* Browser back/forward navigation: when user navigates back to a swapped
   sibling product, reload to ensure correct state (cheaper than full
   restoration of pre-swap DOM). */
window.addEventListener('popstate', function(e) {
  if (e.state && e.state.orwProductSwap) {
    window.location.reload();
  }
});
